import React, { useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import PublicLayout from "@/components/PublicLayout";
import { useLocale, pickLocalized } from "@/contexts/LocaleContext";
import { api, BACKEND_URL } from "@/lib/api";
import { Coffee } from "lucide-react";

function imageUrl(path) {
  if (!path) return null;
  if (path.startsWith("http")) return path;
  return `${BACKEND_URL}${path}`;
}

export default function MenuPage() {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const [menu, setMenu] = useState(null);
  const [activeCat, setActiveCat] = useState(null);

  useEffect(() => {
    api.get("/public/menu").then((r) => {
      setMenu(r.data);
      if (r.data?.categories?.length) {
        const hash = (window.location.hash || "").replace("#", "");
        const match = r.data.categories.find((c) => c.slug === hash);
        setActiveCat((match || r.data.categories[0])?.id || null);
      }
    });
  }, []);

  const cats = useMemo(() => menu?.categories || [], [menu]);
  const items = useMemo(() => {
    if (!menu) return [];
    if (!activeCat) {
      // all
      return Object.values(menu.items_by_category || {}).flat();
    }
    return menu.items_by_category?.[activeCat] || [];
  }, [menu, activeCat]);

  // JSON-LD Menu schema (Schema.org "Menu" type)
  const menuJsonLd = useMemo(() => {
    if (!menu) return null;
    const sections = cats.map((c) => {
      const catItems = menu.items_by_category?.[c.id] || [];
      return {
        "@type": "MenuSection",
        name: pickLocalized(c.name, locale, ""),
        hasMenuItem: catItems.slice(0, 30).map((it) => ({
          "@type": "MenuItem",
          name: pickLocalized(it.name, locale, ""),
          description: pickLocalized(it.description, locale, "") || undefined,
          offers: {
            "@type": "Offer",
            price: Number(it.price).toFixed(2),
            priceCurrency: it.currency || "EUR",
          },
        })),
      };
    }).filter((s) => s.hasMenuItem.length > 0);
    return {
      "@context": "https://schema.org",
      "@type": "Menu",
      name: t("menu_page.title"),
      hasMenuSection: sections,
    };
  }, [menu, cats, locale, t]);

  return (
    <PublicLayout pageTitle={t("menu_page.title")} testid="menu-page">
      {menuJsonLd && (
        <Helmet>
          <script type="application/ld+json">{JSON.stringify(menuJsonLd)}</script>
        </Helmet>
      )}      <section className="max-w-6xl mx-auto px-5 sm:px-8 pt-16 pb-6">
        <p className="text-xs uppercase tracking-[0.3em] text-primary/80">— {t("nav.menu")}</p>
        <h1 className="font-display text-5xl sm:text-6xl mt-3" data-testid="menu-page-title">
          {t("menu_page.title")}
        </h1>
        <p className="text-muted-foreground mt-3 max-w-2xl">{t("menu_page.subtitle")}</p>
      </section>

      <div className="sticky top-16 z-30 bg-background/85 backdrop-blur border-y border-border/60" data-testid="menu-tabs">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 py-3 flex gap-1.5 overflow-x-auto no-scrollbar">
          {cats.map((c) => {
            const isActive = activeCat === c.id;
            return (
              <button
                key={c.id}
                type="button"
                onClick={() => {
                  setActiveCat(c.id);
                  window.history.replaceState({}, "", `#${c.slug}`);
                }}
                data-testid={`menu-tab-${c.slug}`}
                className={`px-4 py-1.5 rounded-full text-sm whitespace-nowrap transition ${
                  isActive
                    ? "bg-primary text-primary-foreground"
                    : "text-muted-foreground hover:text-foreground border border-border"
                }`}
              >
                {pickLocalized(c.name, locale, "")}
              </button>
            );
          })}
        </div>
      </div>

      <section className="max-w-6xl mx-auto px-5 sm:px-8 py-12">
        {items.length === 0 ? (
          <div className="py-20 text-center text-muted-foreground" data-testid="menu-empty">
            <Coffee className="w-10 h-10 mx-auto mb-3 opacity-50" strokeWidth={1.5} />
            {t("menu_page.no_items")}
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="menu-grid">
            {items.map((it) => {
              const img = imageUrl(it.image_url?.md || it.image_url);
              return (
                <article
                  key={it.id}
                  className="group border border-border rounded-2xl bg-card overflow-hidden hover:border-primary/40 transition"
                  data-testid={`menu-item-${it.id}`}
                >
                  <div className="aspect-[5/3] relative bg-gradient-to-br from-primary/10 via-background to-secondary overflow-hidden">
                    {img ? (
                      <img
                        src={img}
                        alt={pickLocalized(it.name, locale, "")}
                        loading="lazy"
                        decoding="async"
                        width="600"
                        height="360"
                        className="absolute inset-0 w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-700"
                      />
                    ) : (
                      <div className="absolute inset-0 flex items-center justify-center text-xs uppercase tracking-widest text-muted-foreground/70">
                        {t("menu_page.no_image")}
                      </div>
                    )}
                  </div>
                  <div className="p-5">
                    <h3 className="font-display text-xl">{pickLocalized(it.name, locale, "")}</h3>
                    <p className="text-sm text-muted-foreground mt-2 min-h-[2.5rem]">
                      {pickLocalized(it.description, locale, "")}
                    </p>
                    <div className="flex items-center justify-between mt-4">
                      <span className="text-primary font-medium" data-testid={`menu-item-price-${it.id}`}>
                        {it.currency === "EUR" ? "€" : it.currency} {Number(it.price).toFixed(2)}
                      </span>
                      {it.allergens?.length > 0 && (
                        <div className="flex flex-wrap gap-1 justify-end">
                          {it.allergens.slice(0, 3).map((a) => (
                            <span
                              key={a}
                              className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full border border-border text-muted-foreground"
                            >
                              {a}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </section>
    </PublicLayout>
  );
}
